/**
 * A tese do produto, em código.
 *
 * A duração de um atendimento não pertence ao serviço, pertence ao par
 * cliente + serviço. "Cabelo e barba" não dura 40 minutos — o João de cabelo e
 * barba dura 40 minutos. O padrão do serviço é só o palpite inicial para quem
 * ainda não tem histórico.
 */

import { differenceInMinutes } from './interval.js';

export const MIN_DURATION_MINUTES = 5;
export const MAX_DURATION_MINUTES = 8 * 60;

export interface DurationResolution {
  minutes: number;
  /** De onde veio o número — a interface mostra isso ao barbeiro. */
  source: 'client' | 'service';
}

/**
 * Duração efetiva: a personalizada do cliente, se existir; senão o padrão do
 * serviço.
 */
export function resolveDuration(input: {
  clientMinutes?: number | null;
  serviceDefaultMinutes: number;
}): DurationResolution {
  const custom = input.clientMinutes;
  if (typeof custom === 'number' && custom > 0) {
    return { minutes: custom, source: 'client' };
  }
  return { minutes: input.serviceDefaultMinutes, source: 'service' };
}

/**
 * Quanto leva o atendimento inteiro: a soma das linhas.
 *
 * Existe como função, e não como um `reduce` solto em cada chamador, porque a
 * regra é uma só e tem de continuar sendo uma só — no dia em que "cabelo e
 * barba juntos levam 5 minutos a menos que separados" virar produto, é aqui
 * que a conta muda, e a agenda inteira acompanha.
 */
export function totalDurationMinutes(items: readonly { minutes: number }[]): number {
  return items.reduce((sum, item) => sum + item.minutes, 0);
}

export function isValidDuration(minutes: number): boolean {
  return (
    Number.isInteger(minutes) && minutes >= MIN_DURATION_MINUTES && minutes <= MAX_DURATION_MINUTES
  );
}

export interface ActualAttendance {
  actualStartAt: Date;
  actualEndAt: Date;
}

/** Minutos realmente gastos, para atendimentos que tiveram início e fim marcados. */
export function actualDurations(attendances: readonly ActualAttendance[]): number[] {
  return attendances
    .map((a) => differenceInMinutes(a.actualEndAt, a.actualStartAt))
    .filter((minutes) => minutes > 0 && minutes <= MAX_DURATION_MINUTES);
}

export interface DurationSuggestion {
  /** O número que propomos gravar. */
  suggestedMinutes: number;
  /** O que está cadastrado hoje. */
  currentMinutes: number;
  /** Média real observada, sem arredondar. */
  observedAverageMinutes: number;
  sampleCount: number;
}

export interface SuggestDurationOptions {
  /** Abaixo disto não há amostra suficiente para incomodar o barbeiro. */
  minSamples?: number;
  /** Diferença mínima para valer um aviso. */
  minDeltaMinutes?: number;
  /** A sugestão sai arredondada — ninguém quer "43 minutos". */
  roundToMinutes?: number;
}

/**
 * Compara o tempo real dos últimos atendimentos com a duração cadastrada e
 * devolve uma sugestão de ajuste — ou `null`, quando não há o que sugerir.
 *
 * O app nunca altera a duração sozinho: uma tarde atípica envenenaria a média e
 * o barbeiro perderia a confiança na agenda. O número continua sendo dele.
 */
export function suggestDurationAdjustment(
  input: { currentMinutes: number; samples: readonly number[] },
  options: SuggestDurationOptions = {},
): DurationSuggestion | null {
  const { minSamples = 3, minDeltaMinutes = 5, roundToMinutes = 5 } = options;

  const samples = input.samples.filter((m) => m > 0 && m <= MAX_DURATION_MINUTES);
  if (samples.length < minSamples) return null;

  const average = samples.reduce((sum, m) => sum + m, 0) / samples.length;
  const suggested = Math.round(average / roundToMinutes) * roundToMinutes;

  if (!isValidDuration(suggested)) return null;
  if (Math.abs(suggested - input.currentMinutes) < minDeltaMinutes) return null;

  return {
    suggestedMinutes: suggested,
    currentMinutes: input.currentMinutes,
    observedAverageMinutes: Math.round(average * 10) / 10,
    sampleCount: samples.length,
  };
}
